import React, { useRef, useState } from "react";
import { FileText, Loader2, Upload, CheckCircle2, AlertTriangle, MessageCircleQuestion } from "lucide-react";
import { api, errorCode, errorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Pill } from "@/components/common";
import { toast } from "sonner";

function List({ icon: Icon, title, items, cls, testid }) {
  if (!items?.length) return null;
  return (
    <div data-testid={testid}><div className="text-xs font-semibold mb-1">{title}</div>
      <ul className="space-y-1">{items.map((x) => <li key={x} className="text-xs flex gap-1.5"><Icon className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${cls}`} />{x}</li>)}</ul></div>
  );
}

export function CvAnalysis({ tariff, onOpen }) {
  const { setUpgradeOpen, refresh } = useAuth();
  const { t } = useI18n();
  const [criteria, setCriteria] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const input = useRef();
  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setRes(null);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("criteria", criteria);
    try {
      const { data } = await api.post("/ai/cv-analysis", fd);
      setRes(data); refresh?.();
      toast.success(t("CV analysed — {{name}} added to your catalog ({{count}} credits)", { name: data.name, count: data.charged_credits }));
    } catch (e) {
      if (["upgrade_required", "insufficient_credits"].includes(errorCode(e))) setUpgradeOpen(true); else toast.error(errorDetail(e));
    } finally { setBusy(false); if (input.current) input.current.value = ""; }
  };
  const a = res?.analysis || {};
  return (
    <div className="card-surface p-5 mb-6" data-testid="cv-analysis-card">
      <div className="flex items-center justify-between mb-2"><div className="eyebrow">{t("CV analysis")}</div><span className="text-xs font-mono font-bold">{t("{{count}} cr.", { count: tariff ?? "…" })}</span></div>
      <div className="grid sm:grid-cols-3 gap-3">
        <Textarea value={criteria} onChange={(e) => setCriteria(e.target.value)} rows={2} className="sm:col-span-2" data-testid="cv-criteria-input"
          placeholder={t("Job criteria (optional) to get a fit score… e.g. ERP project manager, SAP S/4HANA, 5+ years")} />
        <div>
          <input ref={input} type="file" accept=".pdf,.docx,.txt" hidden onChange={(e) => upload(e.target.files?.[0])} data-testid="cv-file-input" />
          <Button variant="outline" className="w-full h-full min-h-[3.5rem]" onClick={() => input.current?.click()} disabled={busy} data-testid="cv-upload-button">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}{busy ? t("Analysis in progress…") : t("Import a CV (PDF, DOCX)")}
          </Button>
        </div>
      </div>
      {res && (
        <div className="mt-4 rounded-xl border p-4 space-y-3" data-testid="cv-analysis-result">
          <div className="flex justify-between gap-3">
            <div className="min-w-0"><button className="font-semibold text-sm hover:underline flex items-center gap-1.5" onClick={() => onOpen(res.candidate_id)} data-testid="cv-open-candidate"><FileText className="w-4 h-4 text-primary" />{res.name}</button>
              <div className="text-xs text-muted-foreground">{[res.title, res.company, res.location].filter(Boolean).join(" · ")}</div></div>
            {a.score != null && <Pill tone={a.score >= 75 ? "green" : a.score >= 50 ? "amber" : "default"} testid="cv-score">{a.score}/100</Pill>}
          </div>
          {a.summary && <p className="text-sm" data-testid="cv-summary">{a.summary}</p>}
          <div className="grid sm:grid-cols-3 gap-3">
            <List icon={CheckCircle2} cls="text-emerald-600" title={t("Strengths")} items={a.strengths} testid="cv-strengths" />
            <List icon={AlertTriangle} cls="text-amber-600" title={t("Points to verify")} items={a.gaps} testid="cv-gaps" />
            <List icon={MessageCircleQuestion} cls="text-primary" title={t("Interview questions")} items={a.questions} testid="cv-questions" />
          </div>
          {res.skills?.length > 0 && <div className="flex flex-wrap gap-1">{res.skills.map((s) => <Pill key={s}>{s}</Pill>)}</div>}
        </div>
      )}
    </div>
  );
}
